import { useState, useEffect, CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookingDetails, ProviderId, TimeSlot } from '../types';
import { getBooking, rescheduleBooking } from '../utils/booking-api';
import { PROVIDERS } from '../constants';
import { detectUserTimezone } from '../utils/timezone';
import Button from '../components/Button';
import Select from '../components/Select';
import Loader from '../components/Loader';
import Modal from '../components/Modal';
import Step3TimeSlot from '../components/steps/Step3TimeSlot';

function formatDateTime(isoString: string, timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      dateStyle: 'full',
      timeStyle: 'short'
    });
    const timezoneName = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(new Date(isoString)).find(part => part.type === 'timeZoneName')?.value || '';

    return `${formatter.format(new Date(isoString))} ${timezoneName}`;
  } catch {
    return isoString;
  }
}

export default function ReschedulePage() {
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedProvider, setSelectedProvider] = useState<ProviderId | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [selectedTimezone, setSelectedTimezone] = useState<string>(detectUserTimezone());

  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isRescheduled, setIsRescheduled] = useState(false);
  const [newBookingDetails, setNewBookingDetails] = useState<any>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (!code) {
      setError('No confirmation code provided. Please check your email for the reschedule link.');
      setIsLoading(false);
      return;
    }

    // Fetch booking details
    getBooking(code)
      .then((bookingData) => {
        setBooking(bookingData);
        // Providers retired from booking (e.g. Michael) can no longer take slots,
        // so start from an active provider the user can confirm or change
        const isActive = PROVIDERS.some((p) => p.id === bookingData.providerId);
        const fallback = PROVIDERS.find((p) => {
          if (bookingData.serviceId === 'demo' && p.id === 'vinil') return false;
          if (bookingData.serviceId !== 'support' && (p.id === 'shawn' || p.id === 'ramon')) return false;
          return true;
        });
        setSelectedProvider(isActive ? bookingData.providerId : (fallback?.id ?? 'samuel'));
        setIsLoading(false);
      })
      .catch((err: any) => {
        setError(err.message || 'Unable to load booking details');
        setIsLoading(false);
      });
  }, []);

  const handleReschedule = async () => {
    if (!booking || !selectedSlot || !selectedTimezone) return;

    setIsRescheduling(true);
    setError(null);

    try {
      const newProviderId = selectedProvider !== booking.providerId ? selectedProvider : undefined;

      const result = await rescheduleBooking({
        code: booking.code,
        newSlot: selectedSlot,
        newProviderId: newProviderId || undefined,
        tz: selectedTimezone
      });

      setNewBookingDetails(result);
      setIsRescheduled(true);
    } catch (err: any) {
      setError(err.message || 'Failed to reschedule booking. Please try again.');
    } finally {
      setIsRescheduling(false);
    }
  };

  const handleBookAnother = () => {
    navigate('/');
  };

  const containerStyles: CSSProperties = {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: 'var(--spacing-2xl) var(--spacing-md)'
  };

  const cardStyles: CSSProperties = {
    background: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-2xl)',
    boxShadow: 'var(--shadow-md)',
    border: '1px solid var(--color-border)',
    marginBottom: 'var(--spacing-xl)'
  };

  const headingStyles: CSSProperties = {
    fontSize: '1.875rem',
    fontWeight: 600,
    color: 'var(--na-navy)',
    marginBottom: 'var(--spacing-lg)',
    textAlign: 'center'
  };

  const sectionStyles: CSSProperties = {
    marginBottom: 'var(--spacing-xl)'
  };

  const labelStyles: CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-xs)',
    display: 'block'
  };

  const valueStyles: CSSProperties = {
    fontSize: '1rem',
    color: 'var(--color-text)',
    marginBottom: 'var(--spacing-md)'
  };

  const buttonGroupStyles: CSSProperties = {
    display: 'flex',
    gap: 'var(--spacing-md)',
    marginTop: 'var(--spacing-2xl)',
    justifyContent: 'center'
  };

  const successMessageStyles: CSSProperties = {
    textAlign: 'center',
    padding: 'var(--spacing-2xl)',
    background: 'var(--color-success-light, #e8f5e9)',
    borderRadius: 'var(--radius-md)',
    marginBottom: 'var(--spacing-xl)'
  };

  const successIconStyles: CSSProperties = {
    fontSize: '3rem',
    marginBottom: 'var(--spacing-md)'
  };

  const currentBookingBoxStyles: CSSProperties = {
    background: 'var(--color-bg-secondary, #f5f5f5)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--spacing-lg)',
    marginBottom: 'var(--spacing-xl)'
  };

  if (isLoading) {
    return (
      <div style={containerStyles}>
        <div style={cardStyles}>
          <Loader text="Loading booking details..." />
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div style={containerStyles}>
        <div style={cardStyles}>
          <h1 style={headingStyles}>Error</h1>
          <p style={{ textAlign: 'center', color: 'var(--color-error)' }}>{error}</p>
          <div style={{ textAlign: 'center', marginTop: 'var(--spacing-xl)' }}>
            <Button onClick={handleBookAnother} variant="primary">
              Book a Meeting
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isRescheduled && newBookingDetails) {
    return (
      <div style={containerStyles}>
        <div style={cardStyles}>
          <div style={successMessageStyles}>
            <div style={successIconStyles}>✓</div>
            <h1 style={headingStyles}>Booking Rescheduled</h1>
            <p>Your meeting has been successfully rescheduled.</p>
          </div>

          {booking && (
            <>
              <div style={sectionStyles}>
                <span style={labelStyles}>Service</span>
                <p style={valueStyles}>{booking.serviceName}</p>
              </div>

              <div style={sectionStyles}>
                <span style={labelStyles}>Provider</span>
                <p style={valueStyles}>{newBookingDetails.providerName}</p>
              </div>

              <div style={sectionStyles}>
                <span style={labelStyles}>New Meeting Time</span>
                <p style={valueStyles}>{formatDateTime(newBookingDetails.slot.start, newBookingDetails.tz)}</p>
              </div>

              {newBookingDetails.confirmationNumber && (
                <div style={sectionStyles}>
                  <span style={labelStyles}>Confirmation Code</span>
                  <p style={valueStyles}>{newBookingDetails.confirmationNumber}</p>
                </div>
              )}
            </>
          )}

          <div style={{ textAlign: 'center' }}>
            <Button onClick={handleBookAnother} variant="primary">
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  const availableProviders = PROVIDERS.filter((p) => {
    // Filter out Vinil for demo service
    if (booking.serviceId === 'demo' && p.id === 'vinil') {
      return false;
    }
    // Shawn and Ramon only take training (support) meetings
    if (booking.serviceId !== 'support' && (p.id === 'shawn' || p.id === 'ramon')) {
      return false;
    }
    return true;
  });

  return (
    <div style={containerStyles}>
      {/* Current Booking Information */}
      <div style={cardStyles}>
        <h1 style={headingStyles}>Reschedule Booking</h1>

        <div style={currentBookingBoxStyles}>
          <h3 style={{ marginBottom: 'var(--spacing-md)', fontSize: '1.125rem', fontWeight: 600 }}>
            Current Booking
          </h3>
          <div style={sectionStyles}>
            <span style={labelStyles}>Service</span>
            <p style={valueStyles}>{booking.serviceName}</p>
          </div>

          <div style={sectionStyles}>
            <span style={labelStyles}>Provider</span>
            <p style={valueStyles}>{booking.providerName}</p>
          </div>

          <div style={sectionStyles}>
            <span style={labelStyles}>Scheduled Time</span>
            <p style={valueStyles}>{formatDateTime(booking.slot.start, selectedTimezone)}</p>
          </div>
        </div>

        <div style={sectionStyles}>
          <Select
            label="Select Provider"
            value={selectedProvider || ''}
            onChange={(e) => {
              setSelectedProvider(e.target.value as ProviderId);
              setSelectedSlot(null); // Reset slot when provider changes
            }}
            options={availableProviders.map((provider) => ({
              value: provider.id,
              label: `${provider.name} - ${provider.title}`
            }))}
            required
          />
        </div>
      </div>

      {/* Time Slot Selection */}
      {selectedProvider && selectedTimezone && (
        <>
          <Step3TimeSlot
            serviceId={booking.serviceId}
            providerId={selectedProvider}
            selectedSlot={selectedSlot}
            selectedTimezone={selectedTimezone}
            onSelectSlot={setSelectedSlot}
            onTimezoneChange={setSelectedTimezone}
          />

          {error && (
            <p style={{ color: 'var(--color-error)', marginTop: 'var(--spacing-md)', textAlign: 'center' }}>
              {error}
            </p>
          )}

          <div style={buttonGroupStyles}>
            <Button
              onClick={handleBookAnother}
              variant="outline"
              disabled={isRescheduling}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReschedule}
              variant="primary"
              disabled={!selectedSlot || isRescheduling}
            >
              {isRescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
            </Button>
          </div>
        </>
      )}

      {/* Loading Modal */}
      <Modal
        isOpen={isRescheduling}
        onClose={() => {}}
        title="Rescheduling Booking"
      >
        <Loader text="Please wait..." />
      </Modal>
    </div>
  );
}
